import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { Check, CircleAlert, ClipboardList, Copy, FileText, Info, Link2, LoaderCircle, Plus, UserRound } from "lucide-react";
import { useSearchParams } from "react-router";

import {
  AcademicRecordFacts,
  AcademicRecordSection,
  AcademicRecordSheet,
} from "@/components/records/AcademicRecordSheet";
import {
  LeadCard,
  PrimaryCourseAction,
  QuietLine,
  SupportSection,
  WithdrawnNotice,
  WorkQueueRow,
} from "@/components/courses/workspace/WorkspacePrimitives";
import {
  AcademicPageHeader,
  AcademicSummaryStrip,
  DataWorkspace,
  WorkspaceState,
} from "@/components/records/AcademicWorkspace";
import { DenseWorkspaceSpecimen } from "@/components/records/DenseWorkspaceSpecimen";
import { RecordTable } from "@/components/records/RecordTable";
import { RegistrarRecordSpecimen } from "@/components/registrar/RegistrarRecordSpecimen";
import {
  RegistrarAuthorityNote,
  RegistrarPanel,
  RegistrarPanelBody,
  RegistrarPanelHeader,
  RegistrarSummaryItem,
  RegistrarSummaryStrip,
} from "@/components/registrar/RegistrarWorkspacePrimitives";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Kbd } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { ListRow, ListRowMenu } from "@/components/ui/list-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Register,
  RegisterCode,
  RegisterDay,
  RegisterHead,
  RegisterMark,
  RegisterNotice,
  RegisterRow,
  RegisterTime,
  RegisterTitle,
} from "@/components/ui/register";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetBody, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Tile, TileHeader } from "@/components/ui/tile";
import { TileTable } from "@/components/ui/tile-table";
import { Toggle } from "@/components/ui/toggle";
import { CourseMark } from "@/components/workspace/CourseMark";
import { Edition } from "@/components/workspace/Edition";
import { EditionBand } from "@/components/workspace/EditionBand";
import { EditionCover } from "@/components/workspace/EditionCover";
import { SeatHomeSpecimen } from "@/components/workspace/SeatHomeSpecimen";
import { PublicControlSpecimen } from "@/components/public/PublicControlSpecimen";
import { SessionIndexSpecimen } from "@/components/session/SessionIndexSpecimen";
import { caretAtEdge, useRovingFocus } from "@/hooks/use-roving-focus";
import type { AttendanceStatus } from "@/lib/api/attendance";
import { formatClassWhen } from "@/lib/seat-workspace";
import { TENANT_SLUGS, type TenantSlug } from "@/lib/theme-presets";
import { cn } from "@/lib/utils";
import { AttendanceStatusGroup, MarkKeysHint } from "@/pages/AttendancePage";
import { BoxesFocus, BoxesSection } from "@/pages/design-system/boxes/BoxesSection";
import { VisitFocus, VisitSection } from "@/pages/design-system/visit/VisitView";
import { applyTenantTheme } from "@/stores/theme-store";
import {
  COMPONENT_ALIASES,
  FOUNDATION_TOKENS,
  TENANT_PRIMITIVES,
  TOKEN_GROUPS,
  TOKEN_PURPOSES,
  type SemanticToken,
  type TokenGroup,
} from "@/styles/token-manifest";

const TENANT_NAMES: Record<TenantSlug, string> = {
  murtada: "Murtada Academy",
  mufid: "Mufid Seminary",
};

const TENANT_LOGOS: Record<TenantSlug, string> = {
  murtada: "/brand/murtada-logo.png",
  mufid: "/brand/mufid-logo.png",
};

function resolveColor(scope: HTMLElement, token: string): string {
  const probe = document.createElement("span");
  probe.style.color = `var(--${token})`;
  probe.style.display = "none";
  scope.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved || getComputedStyle(scope).getPropertyValue(`--${token}`).trim();
}

function channels(color: string): [number, number, number] | null {
  const values = color.match(/[\d.]+/g)?.slice(0, 3).map(Number);
  return values?.length === 3 ? [values[0], values[1], values[2]] : null;
}

function luminance(color: string): number | null {
  const rgb = channels(color);
  if (!rgb) return null;
  const linear = rgb.map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

function ratio(foreground: string, background: string): string {
  const foregroundLuminance = luminance(foreground);
  const backgroundLuminance = luminance(background);
  if (foregroundLuminance === null || backgroundLuminance === null) return "n/a";
  const value = (Math.max(foregroundLuminance, backgroundLuminance) + 0.05) /
    (Math.min(foregroundLuminance, backgroundLuminance) + 0.05);
  return `${value.toFixed(2)}:1`;
}

const CONTRAST_BACKGROUNDS: Partial<Record<SemanticToken, SemanticToken>> = {
  foreground: "background",
  "card-foreground": "card",
  "popover-foreground": "popover",
  "primary-foreground": "primary",
  "secondary-foreground": "secondary",
  "accent-foreground": "accent",
  "destructive-foreground": "destructive",
  "status-success-foreground": "status-success-surface",
  "status-warning-foreground": "status-warning-surface",
  "status-info-foreground": "status-info-surface",
  "sidebar-foreground": "sidebar",
  "sidebar-primary-foreground": "sidebar-primary",
  "sidebar-accent-foreground": "sidebar-accent",
  "topbar-foreground": "topbar",
  "public-foreground": "public-background",
  "public-foreground-soft": "public-background",
  "public-foreground-muted": "public-background",
  "public-primary": "public-background",
  "public-primary-deep": "public-background",
  "public-readable-accent": "public-background-deep",
  "public-readable-highlight": "public-background-deep",
  "public-on-inverse": "public-primary-deep",
  "public-on-inverse-accent": "public-primary-deep",
};

function TokenSwatch({ token, group, scope }: { token: SemanticToken; group: TokenGroup; scope: HTMLElement | null }) {
  const [value, setValue] = useState("");
  const [copied, setCopied] = useState(false);

  useLayoutEffect(() => {
    if (!scope) return;
    const rawValue = getComputedStyle(scope).getPropertyValue(`--${token}`).trim();
    setValue(group === "typography" ? rawValue : resolveColor(scope, token));
  }, [group, scope, token]);

  const contrastBackground = CONTRAST_BACKGROUNDS[token];
  const contrastValue = scope && contrastBackground
    ? ratio(value, resolveColor(scope, contrastBackground))
    : null;

  return (
    <div className="grid min-w-0 grid-cols-[2.5rem_1fr_auto] items-center gap-3 rounded-xl border bg-card p-2.5">
      {group === "typography"
        ? <span className="grid size-10 place-items-center rounded-control border bg-secondary text-section" style={{ fontFamily: `var(--${token})` }}>Aa</span>
        : <span className="size-10 rounded-control border" style={{ background: `var(--${token})` }} />}
      <div className="min-w-0">
        <code className="block truncate text-small font-semibold">--{token}</code>
        <span className="block truncate text-small text-muted-foreground">{value || "resolving..."}</span>
        <span className="block text-small text-muted-foreground">
          {TOKEN_PURPOSES[token] ?? `${token.replaceAll("-", " ")} role`}
          {contrastValue ? ` · ${contrastValue}` : ""}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Copy --${token} and resolved value`}
        onClick={() => {
          void navigator.clipboard?.writeText(`--${token}: ${value}`);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? <Check /> : <Copy />}
      </Button>
    </div>
  );
}

function OwnershipToken({ token, scope, color = false, purpose }: { token: string; scope: HTMLElement | null; color?: boolean; purpose: string }) {
  const [value, setValue] = useState("");
  useLayoutEffect(() => {
    if (!scope) return;
    setValue(color ? resolveColor(scope, token) : getComputedStyle(scope).getPropertyValue(`--${token}`).trim());
  }, [color, scope, token]);
  return (
    <div className="grid min-w-0 grid-cols-[2.25rem_1fr] items-center gap-2 rounded-xl border bg-card p-2">
      {color ? <span className="size-9 rounded-control border" style={{ background: `var(--${token})` }} /> : <span className="grid size-9 place-items-center rounded-control border bg-secondary font-mono text-small">{token.includes("space") ? "↔" : "Aa"}</span>}
      <div className="min-w-0"><code className="block truncate text-key">--{token}</code><span className="block truncate text-small text-muted-foreground">{value || "resolving..."}</span><span className="block text-small text-muted-foreground">{purpose}</span></div>
    </div>
  );
}

function OwnershipLayers({ tenant, scope, idPrefix }: { tenant: TenantSlug; scope: HTMLElement | null; idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-ownership`} className="space-y-4">
      <div><p className="eyebrow">Ownership layers</p><h2 id={`${idPrefix}-ownership`} className="text-tile-title">From shared foundations to components</h2></div>
      <div className="space-y-3">
        <div><h3 className="mb-2 text-section">Shared foundations</h3><div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{FOUNDATION_TOKENS.map((token) => <OwnershipToken key={token} token={token} scope={scope} purpose="Shared non-brand structure" />)}</div></div>
        <div><h3 className="mb-2 text-section">Tenant primitives</h3><div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{TENANT_PRIMITIVES[tenant].map((token) => <OwnershipToken key={token} token={token} scope={scope} color={!token.endsWith("-font")} purpose={`${TENANT_NAMES[tenant]} owned primitive`} />)}</div></div>
        <div><h3 className="mb-2 text-section">Shared component aliases</h3><div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{COMPONENT_ALIASES.map((token) => <OwnershipToken key={token} token={token} scope={scope} color={!token.includes("shadow")} purpose="Derived from a shared semantic role" />)}</div></div>
      </div>
    </section>
  );
}

function FoundationSpecimens({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-foundations-heading`} className="space-y-4">
      <div>
        <p className="eyebrow">Shared foundations</p>
        <h2 id={`${idPrefix}-foundations-heading`} className="text-tile-title">Rhythm, shape, elevation, and motion</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {["space-1", "space-2", "space-4", "space-8"].map((token) => (
          <div key={token} className="rounded-xl border bg-card p-3">
            <code className="text-small">--{token}</code>
            <div className="mt-2 h-3 bg-primary" style={{ width: `var(--${token})` }} />
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {["radius-sm", "radius-md", "radius-lg", "radius-xl"].map((token) => (
          <div key={token} className="h-20 border bg-secondary" style={{ borderRadius: `var(--${token})` }}>
            <code className="p-3 text-small">--{token}</code>
          </div>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl bg-popover p-5" style={{ boxShadow: "var(--elevation-popover)" }}>Popover elevation</div>
        <div className="rounded-xl bg-background p-5" style={{ boxShadow: "var(--elevation-overlay)" }}>Overlay elevation</div>
      </div>
      <div className="rounded-xl border bg-card p-4">
        <div className="h-2 w-24 rounded-full bg-primary transition-[width] hover:w-full motion-reduce:transition-none" style={{ transitionDuration: "var(--motion-slow)", transitionTimingFunction: "var(--ease-standard)" }} />
        <p className="mt-2 text-meta text-muted-foreground">Hover to inspect motion. System reduced-motion collapses all durations to 1ms.</p>
      </div>
    </section>
  );
}

function TypographySpecimens({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-type-heading`} className="space-y-3">
      <div><p className="eyebrow">Typography</p><h2 id={`${idPrefix}-type-heading`} className="text-tile-title">The Bento type ladder</h2></div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border bg-card p-4"><p className="text-title font-semibold" style={{ fontFamily: "var(--ff-display)" }}>Page title: Knowledge with adab</p></div>
        <div className="rounded-xl border bg-card p-4 text-tile-title font-semibold">Tile title: Session plan</div>
        <div className="rounded-xl border bg-card p-4 text-section font-semibold">Section: Published readings</div>
        <div className="rounded-xl border bg-card p-4 text-body">Body: Clear records help students and teachers act with confidence.</div>
        <div className="rounded-xl border bg-card p-4 text-ui">Interface: Open the exact record</div>
        <div className="rounded-xl border bg-card p-4"><p className="eyebrow">Meta: Current term</p><p className="mt-1 text-small text-muted-foreground">Small: Observed September 29</p></div>
        <div className="rounded-xl border bg-card p-4 text-figure font-semibold tabular-nums">1,248</div>
        <div className="rounded-xl border bg-card p-4 font-mono text-ui">Mono: ARB-201 · CRN 1042</div>
        <div className="rounded-xl border bg-card p-4"><span className="text-meta text-muted-foreground">Keyboard key: </span><Kbd>Esc</Kbd></div>
      </div>
    </section>
  );
}

function ControlSpecimens({ idPrefix, tenant }: { idPrefix: string; tenant: TenantSlug }) {
  const nameId = `${idPrefix}-specimen-name`;
  const invalidId = `${idPrefix}-specimen-invalid`;
  const courseId = `${idPrefix}-specimen-course`;
  return (
    <section aria-labelledby={`${idPrefix}-controls-heading`} className="space-y-4">
      <div><p className="eyebrow">Controls and states</p><h2 id={`${idPrefix}-controls-heading`} className="text-tile-title">Keyboard-ready shared primitives</h2></div>
      <div className="flex flex-wrap gap-2">
        <Button>Primary action</Button><Button variant="secondary">Secondary</Button><Button variant="outline">Outline</Button><Button variant="destructive">Delete</Button><Button variant="link">Button link</Button><a href={`#${idPrefix}-data-heading`} className="inline-flex h-9 items-center rounded-control px-2 font-medium text-primary underline underline-offset-4 focus-visible:ring-[var(--focus-width)] focus-visible:ring-ring">Anchor link</a><Button disabled>Disabled</Button><Button disabled><LoaderCircle className="animate-spin" />Loading</Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2"><Label htmlFor={nameId}>Student name</Label><Input id={nameId} placeholder="Fatima Rahman" /></div>
        <div className="space-y-2"><Label htmlFor={invalidId}>Validation state</Label><Input id={invalidId} aria-invalid="true" defaultValue="Invalid record" /><p className="text-meta text-destructive">Choose an active student.</p></div>
        <div className="space-y-2"><Label htmlFor={courseId}>Course</Label><Select defaultValue="arabic"><SelectTrigger id={courseId} className="w-full"><SelectValue /></SelectTrigger><SelectContent data-theme-preset={tenant}><SelectItem value="arabic">Arabic Foundations</SelectItem><SelectItem value="fiqh">Principles of Fiqh</SelectItem></SelectContent></Select></div>
        <div className="flex flex-wrap items-center gap-5 pt-7"><label className="flex items-center gap-2"><Checkbox defaultChecked />Enrolled</label><RadioGroup defaultValue="credit" className="flex"><label className="flex items-center gap-2"><RadioGroupItem value="credit" />Credit</label><label className="flex items-center gap-2"><RadioGroupItem value="audit" />Audit</label></RadioGroup></div>
      </div>
      {/* Academic state is read side by side against the tenant accent on
          purpose: if a status ever starts wearing the brand colour again, the
          two rows below stop being distinguishable at a glance. */}
      <p className="eyebrow pt-2">Academic state</p>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="success">Active</Badge><Badge variant="warning">Leave of absence</Badge><Badge variant="info">Completed</Badge><Badge variant="destructive">Absent</Badge><Badge variant="outline">Withdrawn</Badge>
      </div>
      <p className="eyebrow pt-2">Tenant accent, which never carries state</p>
      <div className="flex flex-wrap items-center gap-2">
        <Badge>Institutional accent</Badge><Badge variant="secondary">Neutral</Badge>
      </div>
    </section>
  );
}

/** Selected #578/#759 needs, rendered through the same kit the app consumes. */
function BentoKitSpecimens({ idPrefix, tenant }: { idPrefix: string; tenant: TenantSlug }) {
  const [lastAction, setLastAction] = useState<string | null>(null);
  const notesId = `${idPrefix}-bento-notes`;
  return (
    <section aria-labelledby={`${idPrefix}-bento-kit-heading`} className="space-y-4" data-bento-kit-specimen>
      <div>
        <p className="eyebrow">Selected Bento kit</p>
        <h2 id={`${idPrefix}-bento-kit-heading`} className="text-tile-title">Rows, fields, and record overlays</h2>
        <p className="mt-2 max-w-3xl text-ui text-muted-foreground">Session materials and work share the same record grammar. Menus and sheets preserve their owner&apos;s actions and return keyboard focus to the opening control.</p>
      </div>
      <Card className="gap-0 py-0">
        <CardHeader className="py-4"><CardTitle as="h3">Session materials and work</CardTitle></CardHeader>
        <ul aria-label="Bento record specimens">
          <ListRow icon={FileText} data-record-id="specimen-material" trailing={(
            <ListRowMenu label="More for Rhetoric reading" data-theme-preset={tenant}>
              <DropdownMenuItem onSelect={() => setLastAction("Reading opened from its record menu.")}>Open reading</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>Publish unavailable</DropdownMenuItem>
            </ListRowMenu>
          )}>
            <p className="text-ui font-semibold">Rhetoric reading: Metaphor and meaning across the classical commentaries</p>
            <p className="text-meta text-muted-foreground">Document · Published · Session 6</p>
          </ListRow>
          <ListRow icon={ClipboardList} data-record-id="specimen-assignment" trailing={<Badge variant="secondary" dot>Published</Badge>}>
            <p className="text-ui font-semibold">Comparative analysis</p>
            <p className="text-meta text-muted-foreground">Attempt 3 · Awaiting review</p>
          </ListRow>
        </ul>
      </Card>
      {lastAction && <QuietLine announce>{lastAction}</QuietLine>}
      <Tabs defaultValue="details">
        <TabsList aria-label="Bento record sections"><TabsTrigger value="details">Details</TabsTrigger><TabsTrigger value="history">History</TabsTrigger></TabsList>
        <TabsContent value="details" className="space-y-4 pt-2">
          <div className="space-y-2"><Label htmlFor={notesId}>Teaching preparation</Label><Textarea id={notesId} placeholder="Private notes for the next session" /></div>
          <Toggle aria-label="Keep preparation pinned" size="sm">Keep pinned</Toggle>
        </TabsContent>
        <TabsContent value="history" className="pt-2"><QuietLine>Attempt 3 is retained with its original identity.</QuietLine></TabsContent>
      </Tabs>
      <div className="flex flex-wrap items-center gap-3">
        <Sheet>
          <SheetTrigger asChild><Button variant="outline">Inspect Bento sheet</Button></SheetTrigger>
          <SheetContent hideDefaultTitle data-theme-preset={tenant} className="w-full sm:max-w-md">
            <SheetHeader><SheetTitle>Exact record context</SheetTitle><SheetDescription>Use <Kbd>Esc</Kbd> to close and return to the opening control.</SheetDescription></SheetHeader>
            <SheetBody className="space-y-4"><QuietLine>The record retains its course, session, and attempt identity.</QuietLine><WithdrawnNotice>Feedback is unavailable. No assessment result is inferred.</WithdrawnNotice></SheetBody>
            <SheetFooter><SheetClose asChild><Button variant="outline">Return to specimen</Button></SheetClose></SheetFooter>
          </SheetContent>
        </Sheet>
        <span className="text-ui text-muted-foreground">Available sheet dismissal: <Kbd>Esc</Kbd></span>
      </div>
      <Alert variant="destructive"><Info aria-hidden="true" /><AlertTitle>This record needs a current confirmed revision before submission can continue</AlertTitle><AlertDescription>Your working text remains available while you resolve the conflict.</AlertDescription></Alert>
    </section>
  );
}

const PAGE_MODEL_PLAN = [
  { minutes: 10, title: "Recitation and review", detail: "Last session's passage, by turns" },
  { minutes: 35, title: "Metaphor in the opening of al-Mutanabbi", detail: "Close reading with the commentary" },
  { minutes: 15, title: "Comparative analysis, in pairs", detail: "Two commentaries on one line" },
] as const;

/** Every notice variant, each where its action would be; never a page banner. */
const ALERT_SPECIMENS = [
  { variant: "default", title: "Records refresh after certification", description: "The register reads the certified result the next time it loads." },
  { variant: "destructive", title: "This attempt could not be submitted", description: "Your working text remains available while you try again." },
  { variant: "warning", title: "One document is pending", description: "The application cannot advance until the office receives it." },
  { variant: "info", title: "Grades release on October 12", description: "Learners see their standing after the release time in the course zone." },
  { variant: "success", title: "Enrollment completed", description: "The learner now holds a seat in the section." },
] as const;

/**
 * The page model every signed-in surface composes (ADR 0029): a page frame
 * with no rule under it, a glance row of stat tiles, then work tiles on the
 * 12-column bento. Rendered through the real primitives so a preset change
 * shows here before any routed page. The second work row holds the three
 * states a tile keeps its place through: loading as skeleton rows under the
 * same header, empty as a quiet line, failure as a notice where the act was.
 */
function PageModelSpecimen({ idPrefix }: { idPrefix: string }) {
  const planId = `${idPrefix}-page-model-plan`;
  const materialsId = `${idPrefix}-page-model-materials`;
  const loadingId = `${idPrefix}-page-model-loading`;
  const emptyId = `${idPrefix}-page-model-empty`;
  const failedId = `${idPrefix}-page-model-failed`;
  return (
    <section aria-labelledby={`${idPrefix}-page-model-heading`} className="space-y-4" data-page-model-specimen>
      <div>
        <p className="eyebrow">Page model</p>
        <h2 id={`${idPrefix}-page-model-heading`} className="text-tile-title">Page frame, glance row, work row</h2>
        <p className="mt-2 max-w-3xl text-ui text-muted-foreground">
          One composition for every signed-in page: the frame names the object and carries at most one
          action, the glance row states its figures, and the work tiles hold its records on the
          12-column bento. A tile keeps its place in every state.
        </p>
      </div>
      <div className="flex flex-col gap-[var(--layout-bento-gap)]">
        <AcademicPageHeader
          eyebrow="My Teaching"
          title="Advanced Arabic Rhetoric"
          description="ARB-301 · Fall 2026 · Tuesday and Thursday, 6:30 PM America/New_York"
          actions={<Button type="button">Open next session</Button>}
        />
        <AcademicSummaryStrip
          label="Course at a glance"
          items={[
            { label: "Sessions", value: 24, detail: "6 remaining" },
            { label: "Students", value: 18, detail: "2 on the waitlist" },
            { label: "Awaiting review", value: 7, detail: "Oldest from September 24" },
            { label: "Attendance", value: "92%", detail: "Last four sessions" },
          ]}
        />
        <div className="grid gap-[var(--layout-bento-gap)] xl:grid-cols-12 xl:items-start">
          <Tile aria-labelledby={planId} className="p-0 xl:col-span-8">
            <TileHeader
              id={planId}
              title="Session plan"
              count={PAGE_MODEL_PLAN.length}
              className="ps-5 pe-3 pt-3.5 pb-2.5"
              action={<Button type="button" variant="subtle" size="sm">Edit plan</Button>}
            />
            <ol>
              {PAGE_MODEL_PLAN.map((item) => (
                <li key={item.title} className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-x-3 border-t border-rule px-5 py-3">
                  <span className="text-ui tabular-nums text-muted-foreground">{item.minutes} min</span>
                  <span className="min-w-0">
                    <span className="block text-ui font-semibold text-foreground">{item.title}</span>
                    <span className="block text-meta text-muted-foreground">{item.detail}</span>
                  </span>
                </li>
              ))}
            </ol>
          </Tile>
          <Tile aria-labelledby={materialsId} className="p-0 xl:col-span-4">
            <TileHeader
              id={materialsId}
              title="Materials"
              count={2}
              className="ps-5 pe-3 pt-3.5 pb-2.5"
              action={<Button type="button" variant="subtle" size="sm"><Plus aria-hidden="true" />Add</Button>}
            />
            <ul className="pb-1.5">
              <ListRow icon={FileText}>
                <p className="text-ui font-semibold">Commentary on the opening of al-Mutanabbi</p>
                <p className="text-meta text-muted-foreground">Document · Published</p>
              </ListRow>
              <ListRow icon={Link2}>
                <p className="text-ui font-semibold">Recording of Session 5</p>
                <p className="text-meta text-muted-foreground">Link · Published</p>
              </ListRow>
            </ul>
          </Tile>
        </div>
        <div className="grid gap-[var(--layout-bento-gap)] xl:grid-cols-12 xl:items-start" data-page-model-states>
          <Tile aria-labelledby={loadingId} aria-busy="true" className="gap-3 xl:col-span-4">
            <TileHeader id={loadingId} title="Loading" />
            <WorkspaceState kind="loading" description="Loading student work" className="px-0 py-0 sm:px-0" />
          </Tile>
          <Tile aria-labelledby={emptyId} className="gap-3 xl:col-span-4">
            <TileHeader
              id={emptyId}
              title="Empty"
              count={0}
              action={<Button type="button" variant="subtle" size="sm"><Plus aria-hidden="true" />Add</Button>}
            />
            <WorkspaceState
              kind="empty"
              description="No materials are published for this session yet."
              className="px-0 py-0 sm:px-0"
            />
          </Tile>
          <Tile aria-labelledby={failedId} className="gap-3 xl:col-span-4">
            <TileHeader id={failedId} title="Failure" />
            <Alert variant="destructive">
              <CircleAlert aria-hidden="true" />
              <AlertTitle>Attendance could not be saved</AlertTitle>
              <AlertDescription>Your marks are kept on this page. Try again when the connection returns.</AlertDescription>
            </Alert>
          </Tile>
        </div>
      </div>
      <div className="grid gap-3" data-alert-variants-specimen>
        <p className="eyebrow">Notices, each where its action is</p>
        {ALERT_SPECIMENS.map((notice) => (
          <Alert key={notice.variant} variant={notice.variant}>
            <Info aria-hidden="true" />
            <AlertTitle>{notice.title}</AlertTitle>
            <AlertDescription>{notice.description}</AlertDescription>
          </Alert>
        ))}
      </div>
    </section>
  );
}

/**
 * The canvas data set (ADR 0035). Six subjects whose prefixes hash to the six
 * hues and whose numbers land on the six motifs, so the set shows the whole
 * generator; the register carries the canvas stress set as drawn.
 */
const SURFACE_COVERS = [
  { code: "FQH 110", label: "Fiqh", title: "Introduction to Fiqh" },
  { code: "HIS 240", label: "History", title: "Islamic History from the Minor Occultation to the Rise of the Safavid State" },
  { code: "ARB 201", label: "Arabic", title: "Classical Arabic II" },
  { code: "LGC 210", label: "Logic", title: "Introduction to Logic" },
  { code: "SRH 140", label: "Sirah", title: "The Prophetic Biography" },
  { code: "QUR 101", label: "Qur'an", title: "Qur'anic Recitation" },
];

const SURFACE_REGISTER = [
  { code: "FQH 110", title: "Introduction to Fiqh", notice: "4 submissions to review", day: "Today", today: true, time: "2:30 PM" },
  { code: "ARB 201", title: "Classical Arabic II", notice: null, day: "Tomorrow", today: false, time: "6:30 PM" },
  { code: "QUR 101", title: "Qur'anic Recitation", notice: null, day: "Sun, Oct 11", today: false, time: "10:00 AM" },
  { code: "QUR 102", title: "Qur'anic Recitation II", notice: null, day: "Sun, Oct 11", today: false, time: "11:30 AM" },
  { code: "HAD 220", title: "Sciences of Hadith", notice: null, day: "Tue, Oct 13", today: false, time: "4:30 PM" },
  { code: "HIS 240", title: "Islamic History from the Minor Occultation to the Rise of the Safavid State", notice: null, day: "Wed, Oct 14", today: false, time: "6:30 PM" },
];

const SURFACE_PEOPLE = [
  { name: "Amina Rahman", role: "Student", access: "Active", notice: null },
  { name: "Bilal Hussain", role: "Student", access: "Invited", notice: "Invitation expires Friday" },
];

const COURSE_VIEWS = ["Plan", "Coursework", "Materials", "People", "Grades"];

function SurfaceGrammarSpecimen({ idPrefix }: { idPrefix: string }) {
  const headingId = `${idPrefix}-surface-grammar-heading`;
  const registerId = `${idPrefix}-surface-grammar-register`;
  const href = `#${headingId}`;
  // A fixed clock, so the time syntax reads the same on every machine.
  const reference = "2026-10-09T15:00:00Z";
  const zone = "America/Chicago";
  return (
    <section aria-labelledby={headingId} className="space-y-4" data-surface-grammar-specimen>
      <div>
        <p className="eyebrow">Surface grammar</p>
        <h2 id={headingId} className="text-tile-title">One course object at three scales</h2>
        <p className="mt-2 max-w-3xl text-ui text-muted-foreground">
          The subject picks one of six hues, the number one of six motifs, and one signature draws the
          cover, the 40 and 20 px marks. An edition where the user chooses among a few; a band where the
          user is inside one; a register row where the user scans many. People take none of the three.
        </p>
      </div>
      <ul aria-label="Six signatures" className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6" data-cover-set>
        {SURFACE_COVERS.map((course) => (
          <li key={course.code} className="flex min-w-0 flex-col gap-2.5">
            <EditionCover code={course.code} label={course.label} />
            <div className="flex min-w-0 items-center gap-2.5">
              <CourseMark code={course.code} size="lg" />
              <CourseMark code={course.code} size="sm" />
              <span className="flex min-w-0 flex-col">
                <span className="font-mono text-meta font-medium tabular-nums text-muted-foreground">{course.code}</span>
                <span className="truncate text-meta font-semibold">{course.title}</span>
              </span>
            </div>
          </li>
        ))}
      </ul>
      <div className="grid gap-[var(--layout-bento-gap)] xl:grid-cols-12 xl:items-start">
        <ul aria-label="Editions" className="-mx-2 grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 xl:col-span-4">
          <li>
            <Edition
              href={href}
              code="HIS 240"
              label="History"
              title="Islamic History from the Minor Occultation to the Rise of the Safavid State"
              meta={<>Next session · {formatClassWhen("2026-10-14T23:30:00Z", zone, reference)}</>}
            />
          </li>
        </ul>
        <div className="flex flex-col gap-[var(--layout-bento-gap)] xl:col-span-8">
          <Tile as="div" aria-label="Course band" className="gap-4" data-band-specimen>
            <EditionBand
              code="ARB 201"
              label="Arabic"
              eyebrow="Course"
              title="Classical Arabic II"
              meta="Fall 2026 · Primary instructor · CRN 10422 · Times in Chicago"
              action={<Button type="button" variant="subtle" size="sm">Course info</Button>}
            />
            <nav aria-label="Course views" className="flex gap-6 border-b border-border">
              {COURSE_VIEWS.map((view) => (
                <a
                  key={view}
                  href={href}
                  aria-current={view === "Coursework" ? "page" : undefined}
                  className={cn(
                    "-mb-px border-b-2 py-[0.4375rem] text-ui transition-colors duration-[var(--motion-fast)]",
                    view === "Coursework"
                      ? "border-foreground font-semibold text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {view}
                </a>
              ))}
            </nav>
          </Tile>
          <Tile as="div" aria-label="Session band" data-band-specimen>
            <EditionBand
              code="ARB 201"
              label="Arabic"
              eyebrow="Classical Arabic II · Fall 2026"
              title="Session 7"
              meta="Tue, Oct 13 · 6:30 to 8:00 PM · Room 2 · Chicago time"
            />
          </Tile>
        </div>
      </div>
      <Register
        template="1.25rem 4.5rem minmax(0,1fr) auto 7rem 4.5rem"
        aria-labelledby={registerId}
        head={<RegisterHead id={registerId} title="Your courses" count={SURFACE_REGISTER.length} action={<Button type="button" variant="subtle" size="sm">All sessions</Button>} />}
      >
        {SURFACE_REGISTER.map((row) => (
          <RegisterRow key={row.code} href={href}>
            <RegisterMark code={row.code} />
            <RegisterCode>{row.code}</RegisterCode>
            <RegisterTitle>{row.title}</RegisterTitle>
            <RegisterNotice>{row.notice}</RegisterNotice>
            <RegisterDay today={row.today}>{row.day}</RegisterDay>
            <RegisterTime>{row.time}</RegisterTime>
          </RegisterRow>
        ))}
      </Register>
      <Register
        template="auto minmax(0,1fr) auto 6rem 5rem"
        aria-label="People"
        head={<RegisterHead><span /><span>Name</span><span /><span>Role</span><span className="text-end">Access</span></RegisterHead>}
      >
        {SURFACE_PEOPLE.map((person) => (
          <RegisterRow key={person.name}>
            <Checkbox aria-label={`Select ${person.name}`} />
            <RegisterTitle href={href}>{person.name}</RegisterTitle>
            <RegisterNotice>{person.notice}</RegisterNotice>
            <span className="text-ui text-foreground-soft">{person.role}</span>
            <span className="text-end text-ui text-foreground">{person.access}</span>
          </RegisterRow>
        ))}
      </Register>
    </section>
  );
}

function WeightVoiceSpecimens({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-weight-voices-heading`} className="space-y-4">
      <div>
        <p className="eyebrow">Course composition contract</p>
        <h2 id={`${idPrefix}-weight-voices-heading`} className="text-tile-title">
          Five weight voices, one reading order
        </h2>
        <p className="mt-2 max-w-3xl text-ui text-muted-foreground">
          Identity, the session, work requiring judgement, quiet course context, and a withdrawn read
          remain in this DOM order at every width. The specimen uses long institutional strings so
          wrapping is visible before a real course discovers it.
        </p>
      </div>
      <div
        className="space-y-6"
        data-workspace-weight-specimen
        data-grayscale-hierarchy="lead-support-quiet-withdrawn"
      >
        <LeadCard
          id={`${idPrefix}-weight-lead`}
          eyebrow="Next exact session"
          title="Advanced Arabic Rhetoric: Metaphor, Ellipsis, and Meaning Across Classical Commentaries"
          action={(
            <PrimaryCourseAction
              action={{
                label: "Open session",
                href: `#${idPrefix}-weight-lead`,
                reason: "open_ranked_exact_class",
              }}
            />
          )}
        >
          <p className="text-ui text-foreground">
            Seminar discussion with the complete teaching team and the enrolled section.
          </p>
          <p className="mt-2 font-mono text-meta text-muted-foreground">
            Tuesday, September 29, 2026 · 6:30 PM America/New_York
          </p>
          <p className="mt-2 text-ui text-muted-foreground">
            A session destination is published. IMAM does not claim that the provider will admit every learner.
          </p>
        </LeadCard>
        <SupportSection
          id={`${idPrefix}-weight-support`}
          label="Waiting on you"
          description="The oldest immutable learner attempt remains first; the row does not advance to a later submission."
        >
          <WorkQueueRow
            identity="Maryam bint Abdullah · Comparative analysis of Qur'anic rhetorical structures"
            evidence="Attempt 3 · accepted September 24, 2026 · feedback has not yet been released"
            state="Exact attempt identity: SUB-2026-09-24-003"
            action={{ href: `#${idPrefix}-weight-support`, label: "Review attempt" }}
            primaryActionHref="/design-system#another-ranked-action"
            recordId="SUB-2026-09-24-003"
            attemptNumber={3}
          />
        </SupportSection>
        <QuietLine
          disclosure={{
            label: "A session destination is published; read the recorded provenance.",
            children: (
              <p>
                The course default was recorded by authorized staff on September 18, 2026. Provider
                admission and attendance remain outside this statement.
              </p>
            ),
          }}
        >
          Six upcoming sessions have a published destination, shown in each course&apos;s named time zone.
        </QuietLine>
        <WithdrawnNotice>
          Published resources are unavailable, so no resource count is shown and no empty library is implied.
        </WithdrawnNotice>
      </div>
    </section>
  );
}

/**
 * The registrar consumes the same #543 primitives rather than demonstrating a
 * second hierarchy. This composition keeps the four state meanings beside one
 * another because a happy-path fixture cannot prove that an unavailable read
 * stays different from a real zero, or that history stays different from both.
 */
function RegistrarWeightVoiceSpecimen({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-registrar-weight-heading`} className="space-y-4">
      <div>
        <p className="eyebrow">Registrar composition contract</p>
        <h2 id={`${idPrefix}-registrar-weight-heading`} className="text-tile-title">
          One institutional voice, one dominant action
        </h2>
        <p className="mt-2 max-w-3xl text-ui text-muted-foreground">
          The registrar frame widens this exact sequence without reordering it. Institutional facts
          stay above register mechanics, deletion stays outlined, and failed reads never become zero.
        </p>
      </div>

      <div
        className="space-y-6"
        data-registrar-composition-specimen
        data-grayscale-hierarchy="identity-lead-support-quiet-withdrawn-historical"
      >
        <header
          className="border-b border-border pb-4"
          data-registrar-stratum="identity"
        >
          <p className="eyebrow">Registrar workspace</p>
          <h3 className="mt-1 text-tile-title font-semibold text-foreground">
            Admissions and institutional access
          </h3>
          <p className="mt-2 max-w-2xl text-ui text-muted-foreground">
            One record owner remains visible while the registrar carries an admitted learner into access.
          </p>
        </header>

        <div data-registrar-stratum="lead">
          <LeadCard
            id={`${idPrefix}-registrar-lead`}
            eyebrow="Admitted applicant"
            title="Sana Noor · Fall 2026 evening program"
            action={(
              <div className="flex flex-col items-stretch gap-2 sm:items-end">
                <PrimaryCourseAction
                  action={{
                    label: "Convert to student",
                    href: `#${idPrefix}-registrar-lead`,
                    reason: "convert_admitted_applicant",
                  }}
                />
                <Button type="button" variant="outline" className="min-h-11">Send invitation</Button>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 border-destructive text-destructive hover:bg-destructive-surface hover:text-destructive"
                >
                  Delete applicant
                </Button>
              </div>
            )}
          >
            <p className="text-ui text-foreground">
              Admission is recorded. Conversion creates the institution-owned student record.
            </p>
            <p className="mt-2 text-ui text-muted-foreground">
              Invitation and account activation do not create enrollment, teaching assignment, or history.
            </p>
          </LeadCard>
        </div>

        <RegistrarSummaryStrip
          aria-label="Registrar institutional summary"
          data-registrar-stratum="summary"
        >
          <RegistrarSummaryItem label="Matching people" value={60} detail="Server-filtered total" />
          <RegistrarSummaryItem label="Teaching scope" value="All" detail="All terms" />
        </RegistrarSummaryStrip>

        <RegistrarPanel
          aria-label="Registrar supporting record"
          data-registrar-stratum="support"
        >
          <RegistrarPanelHeader>
            <p className="eyebrow">Supporting record</p>
            <p className="mt-1 font-medium text-foreground">Post-admission handoff</p>
          </RegistrarPanelHeader>
          <RegistrarPanelBody>
            <p className="text-ui text-muted-foreground">
              Each line reads its own institutional record. This is not a second workflow status.
            </p>
          </RegistrarPanelBody>
        </RegistrarPanel>

        <div className="space-y-3" data-registrar-stratum="states">
          <div data-registrar-state="ready">
            <QuietLine>Ready: the student record is active and its owning facts are available.</QuietLine>
          </div>
          <div data-registrar-state="empty">
            <WorkspaceState
              kind="empty"
              title="No people match this view"
              description="The server found no institutional record in this result set."
            />
          </div>
          <div data-registrar-state="unavailable">
            <WorkspaceState
              kind="error"
              title="The People register is unavailable"
              description="No row or access state was inferred from the failed request."
              onRetry={() => undefined}
            />
          </div>
          <div data-registrar-state="historical">
            <QuietLine>Historical: this closed-term record remains readable and keeps its provenance.</QuietLine>
          </div>
        </div>

        <RegistrarAuthorityNote tone="neutral">
          Registrar oversight only - no teaching controls
        </RegistrarAuthorityNote>
      </div>
    </section>
  );
}

function ApplicationSpecimens({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-application-heading`} className="space-y-4">
      <div><p className="eyebrow">Application components</p><h2 id={`${idPrefix}-application-heading`} className="text-tile-title">Public and authenticated surfaces</h2></div>
      <div className="overflow-hidden rounded-xl border">
        <div className="flex items-center justify-between bg-sidebar px-4 py-3 text-sidebar-foreground"><span className="text-section font-semibold">Scholar Hub</span><Badge className="bg-sidebar-primary text-sidebar-primary-foreground">Fall 2026</Badge></div>
        <div className="app-topbar static"><strong>Academic records</strong><span className="ml-auto text-meta text-muted-foreground">Signed in</span></div>
        <div className="grid gap-4 bg-background p-4 md:grid-cols-2">
          <Card><CardHeader><CardTitle as="h3">Arabic Foundations</CardTitle><CardDescription>Monday and Wednesday</CardDescription></CardHeader><CardContent className="flex items-center gap-2"><Badge variant="success">Published</Badge><span className="text-meta text-muted-foreground">18 students</span></CardContent></Card>
          <Card><CardHeader><CardTitle as="h3">Enrollment</CardTitle></CardHeader><CardContent><Table><TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody><TableRow><TableCell>Amina Yusuf</TableCell><TableCell><Badge variant="secondary">Active</Badge></TableCell></TableRow></TableBody></Table></CardContent></Card>
        </div>
      </div>
      <div className="rounded-xl border bg-[var(--public-background)] p-6 text-[var(--public-foreground)]"><p className="eyebrow">Public surface</p><h3 className="mt-1 text-tile-title">Begin your learning journey</h3><p className="mt-2 text-[var(--public-foreground-soft)]">A representative marketing panel using only public semantic roles.</p><Button className="mt-4">Apply now</Button></div>
      <div className="grid gap-3 md:grid-cols-3">
        <Alert><Info /><AlertTitle>Information</AlertTitle><AlertDescription>Records refresh after certification.</AlertDescription></Alert>
        <div className="rounded-xl border bg-[var(--status-success-surface)] p-4 text-[var(--status-success-foreground)]"><strong>Success</strong><p className="text-ui">Enrollment completed.</p></div>
        <div className="rounded-xl border bg-[var(--status-warning-surface)] p-4 text-[var(--status-warning-foreground)]"><strong>Attention</strong><p className="text-ui">One document is pending.</p></div>
      </div>
      <div className="flex flex-wrap gap-3"><div className="flex items-center gap-2 rounded-control border bg-card py-1.5 pr-3 pl-1.5"><span className="grid size-8 place-items-center rounded-full bg-primary text-primary-foreground"><UserRound className="size-4" /></span><span><strong className="block text-meta">Amina Yusuf</strong><span className="block text-small text-muted-foreground">Student</span></span></div><div className="min-w-52 space-y-2 rounded-xl border bg-card p-3"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-2/3" /></div><div className="rounded-xl border border-dashed bg-card p-4 text-center"><strong>No assignments yet</strong><p className="text-meta text-muted-foreground">New work will appear here.</p></div></div>
    </section>
  );
}

const RECORD_SPECIMEN_ROWS = [
  { id: "sp-1", name: "Amina Yusuf", program: "Arabic Foundations", standing: "Active", gpa: 3.8 },
  { id: "sp-2", name: "Hasan Naqvi", program: "Principles of Fiqh", standing: "On leave", gpa: 3.1 },
  { id: "sp-3", name: "Zaynab Ali", program: "Arabic Foundations", standing: "Active", gpa: null },
];

/**
 * The records layer, which the specimen previously omitted — it showed the
 * tokens and the primitives and stopped at the experience-pattern boundary,
 * which is the layer product screens actually compose on.
 *
 * Every capability is on at once here, which no real page does. That is the
 * point of a specimen: the sort affordance, the facet chip, the column menu,
 * the selection bar, and the pager have to be inspectable together, in both
 * presets, without opening a register and filtering it into the right state.
 *
 * A registrar record is a page of its own (one address per record); its
 * specimen is a section of its own further down, outside this list region.
 * The sheet below stays for the records that remain sheets over a list
 * (history evidence, imports, structure). A single id drives the row's selected treatment and the
 * sheet's open state, as `useRecordSheetSelection` does where a sheet remains.
 */
function RecordSpecimens({ idPrefix }: { idPrefix: string }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sheetWidth, setSheetWidth] = useState<"standard" | "wide">("standard");
  const selected = RECORD_SPECIMEN_ROWS.find((row) => row.id === selectedId) ?? null;
  return (
    <section data-registrar-record-specimen aria-labelledby={`${idPrefix}-records-heading`} className="space-y-4">
      <div>
        <p className="eyebrow">Experience patterns</p>
        <h2 id={`${idPrefix}-records-heading`} className="text-tile-title font-semibold">Registrar record grammar</h2>
      </div>
      <DataWorkspace
        title="The register"
        description="One tile, quiet facets, separator rows, and an exact record sheet. All capabilities are enabled here."
        resultLabel={`${RECORD_SPECIMEN_ROWS.length} records shown`}
      >
        <RecordTable
          rows={RECORD_SPECIMEN_ROWS}
          rowId={(row) => row.id}
          selectedId={selectedId}
          onOpen={(id) => {
            setSheetWidth("standard");
            setSelectedId(id);
          }}
          sortable
          selectable
          columnVisibility
          pageSize={2}
          // Two facets, with their buckets declared rather than derived. A
          // derived bucket is built from the rows the *other* facets have left,
          // so an option that would empty the list is never offered and the
          // empty state cannot be reached from the specimen at all — which is
          // exactly the state the selection bar has to survive.
          facets={[
            {
              id: "standing",
              label: "Standing",
              value: (row) => row.standing,
              options: [{ value: "Active" }, { value: "On leave" }],
            },
            {
              id: "program",
              label: "Program",
              value: (row) => row.program,
              options: [{ value: "Arabic Foundations" }, { value: "Principles of Fiqh" }],
            },
          ]}
          bulkActions={<Button size="sm" variant="outline">Advance stage</Button>}
          emptyState={(
            <div className="px-5 py-10 text-center text-ui text-muted-foreground">
              No records match this filter.
            </div>
          )}
          primary={{
            header: "Student",
            label: (row) => row.name,
            detail: (row) => row.program,
            sortValue: (row) => row.name.toLocaleLowerCase(),
          }}
          columns={[
            {
              id: "standing",
              header: "Standing",
              sortValue: (row) => row.standing,
              cell: (row) => (
                <Badge variant={row.standing === "Active" ? "success" : "secondary"}>{row.standing}</Badge>
              ),
            },
            { id: "program", header: "Program", cell: (row) => row.program },
            {
              id: "gpa",
              header: "GPA",
              align: "right",
              cellClassName: "font-medium",
              sortValue: (row) => row.gpa,
              cell: (row) => row.gpa ?? "—",
            },
          ]}
          card={(row) => (
            <>
              <div className="flex items-start justify-between gap-3">
                <span className="truncate font-medium">{row.name}</span>
                <Badge variant={row.standing === "Active" ? "success" : "secondary"}>{row.standing}</Badge>
              </div>
              <div className="mt-3 flex justify-between text-meta text-muted-foreground">
                <span className="truncate">{row.program}</span>
                <span>GPA {row.gpa ?? "—"}</span>
              </div>
            </>
          )}
        />
      </DataWorkspace>
      <AcademicRecordSheet
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        eyebrow="Student record"
        title={selected?.name ?? ""}
        description={selected ? `${selected.program} · ${selected.standing}` : ""}
        status={selected && (
          <Badge variant={selected.standing === "Active" ? "success" : "secondary"}>
            {selected.standing}
          </Badge>
        )}
        actions={selected && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setSheetWidth((current) => current === "standard" ? "wide" : "standard")}
          >
            {sheetWidth === "standard" ? "Show wide record" : "Show standard record"}
          </Button>
        )}
        width={sheetWidth}
      >
        <AcademicRecordSection
          eyebrow="Specimen record"
          title="Academic standing"
          description="The same three fields the row carries, in the form the record sheet gives them."
        >
          <AcademicRecordFacts
            items={[
              { label: "Program", value: selected?.program ?? "—" },
              { label: "Standing", value: selected?.standing ?? "—" },
              { label: "GPA", value: selected?.gpa ?? "—" },
            ]}
          />
        </AcademicRecordSection>
      </AcademicRecordSheet>
    </section>
  );
}

const SPECIMEN_LEARNERS = ["Amina Rahman", "Bilal Hussain", "Khadija Noor"];
const SPECIMEN_ASSESSMENTS = ["Reading response", "Seminar paper"];
const SPECIMEN_SAVED = [["88", "91"], ["76", ""], ["93", "85"]];

/**
 * The gradebook's cell model on specimen data: the arrows walk the grid,
 * Enter moves down, Shift+Enter up, Escape restores the saved value. Nothing
 * is saved here; the gradebook's blur save belongs to the surface.
 */
function KeyboardGridSpecimen({ idPrefix }: { idPrefix: string }) {
  const [scores, setScores] = useState(() => SPECIMEN_SAVED.map((row) => [...row]));
  const columns = SPECIMEN_ASSESSMENTS.length;
  const count = SPECIMEN_LEARNERS.length * columns;
  const grid = useRovingFocus<HTMLInputElement>({ count, orientation: "grid", columns, roving: false, revealInTable: true, onMove: (_, input) => input.select() });
  const onKey = (event: KeyboardEvent<HTMLInputElement>, index: number, row: number, column: number) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === "Enter") {
      event.preventDefault();
      const target = index + (event.shiftKey ? -columns : columns);
      if (target >= 0 && target < count) grid.focus(target)?.select();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setScores((held) => held.map((values, r) => (r === row ? values.map((value, c) => (c === column ? SPECIMEN_SAVED[r][c] : value)) : values)));
      return;
    }
    if ((event.key === "ArrowLeft" || event.key === "ArrowRight") && !caretAtEdge(event.currentTarget, event.key)) return;
    grid.itemProps(index).onKeyDown(event);
  };
  return (
    <Tile aria-labelledby={`${idPrefix}-keyboard-grid`} className="gap-3">
      <TileHeader id={`${idPrefix}-keyboard-grid`} as="h3" title="Scores by assessment" />
      <p className="hidden flex-wrap items-center gap-x-1.5 gap-y-1 text-meta text-muted-foreground pointer-fine:flex">
        <Kbd>Enter</Kbd> saves and moves down, <Kbd>Shift</Kbd><Kbd>Enter</Kbd> moves up,
        <Kbd>Esc</Kbd> restores the saved score.
      </p>
      <TileTable scrollLabel="Specimen scores by assessment" stickyHeader>
        <TableHeader><TableRow><TableHead className="sticky left-0 z-20 bg-card">Student</TableHead>{SPECIMEN_ASSESSMENTS.map((label) => <TableHead key={label} className="text-center">{label}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{SPECIMEN_LEARNERS.map((learner, row) => (
          <TableRow key={learner}>
            <TableCell className="sticky left-0 z-10 bg-card font-medium text-foreground">{learner}</TableCell>
            {SPECIMEN_ASSESSMENTS.map((label, column) => {
              const index = row * columns + column;
              const cell = grid.itemProps(index);
              return (
                <TableCell key={label} className="text-center">
                  <Input
                    ref={cell.ref}
                    aria-label={`${learner} — ${label}`}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={scores[row][column]}
                    onFocus={cell.onFocus}
                    onKeyDown={(event) => onKey(event, index, row, column)}
                    onChange={(event) => {
                      const next = event.target.value;
                      if (!/^\d*\.?\d*$/.test(next)) return;
                      setScores((held) => held.map((values, r) => (r === row ? values.map((value, c) => (c === column ? next : value)) : values)));
                    }}
                    className="w-16 text-center font-data tabular-nums"
                    placeholder="—"
                  />
                </TableCell>
              );
            })}
          </TableRow>
        ))}</TableBody>
      </TileTable>
    </Tile>
  );
}

/** The quick mark's rows, through the surface's own radio group. */
function AttendanceRowSpecimen({ idPrefix }: { idPrefix: string }) {
  const [marks, setMarks] = useState<Record<string, AttendanceStatus>>({ [SPECIMEN_LEARNERS[0]]: "present" });
  const groups = useRef<(HTMLDivElement | null)[]>([]);
  return (
    <Tile aria-labelledby={`${idPrefix}-attendance-rows`} className="gap-3">
      <TileHeader id={`${idPrefix}-attendance-rows`} as="h3" title="Quick mark" />
      <MarkKeysHint className="text-meta text-muted-foreground" />
      <TileTable scrollLabel="Specimen session attendance">
        <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>{SPECIMEN_LEARNERS.map((learner, row) => (
          <TableRow key={learner}>
            <TableCell className="font-medium text-foreground">{learner}</TableCell>
            <TableCell>
              <AttendanceStatusGroup
                groupRef={(node) => { groups.current[row] = node; }}
                name={learner}
                status={marks[learner]}
                disabled={false}
                onSelect={(status) => setMarks((held) => ({ ...held, [learner]: status }))}
                onAdvance={() => {
                  const stop = groups.current[row + 1]?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]');
                  stop?.focus();
                  return Boolean(stop);
                }}
              />
            </TableCell>
          </TableRow>
        ))}</TableBody>
      </TileTable>
    </Tile>
  );
}

const SPECIMEN_CONVERSATIONS = [
  { subject: "Question about the reading", person: "Amina Rahman", when: "Oct 6, 2026, 9:12 AM" },
  { subject: "Seminar paper outline", person: "Bilal Hussain", when: "Oct 5, 2026, 4:40 PM" },
  { subject: "Absence next week", person: "Khadija Noor", when: "Oct 3, 2026, 11:05 AM" },
];

/** The inbox list's model: one tab stop, the arrows, Home and End, Enter or Space to open. */
function RovingListSpecimen({ idPrefix }: { idPrefix: string }) {
  const [current, setCurrent] = useState(0);
  const list = useRovingFocus<HTMLButtonElement>({ count: SPECIMEN_CONVERSATIONS.length, orientation: "vertical", activeIndex: current });
  return (
    <Tile aria-labelledby={`${idPrefix}-roving-list`} className="gap-3">
      <TileHeader id={`${idPrefix}-roving-list`} as="h3" title="Conversations" count={SPECIMEN_CONVERSATIONS.length} />
      <ul className="-ms-3 -me-2" aria-labelledby={`${idPrefix}-roving-list`}>
        {SPECIMEN_CONVERSATIONS.map((item, index) => (
          <li key={item.subject} className="border-t border-rule">
            <button
              type="button"
              {...list.itemProps(index)}
              aria-current={current === index ? "page" : undefined}
              onClick={() => setCurrent(index)}
              className={cn(
                "grid w-full gap-1 rounded-control px-3 py-3 text-left transition-colors duration-[var(--motion-fast)] hover:bg-well focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                current === index && "bg-primary-wash",
              )}
            >
              <span className="text-ui font-medium text-foreground">{item.subject}</span>
              <span className="text-ui">{item.person}</span>
              <span className="font-data text-small tabular-nums text-muted-foreground">{item.when}</span>
            </button>
          </li>
        ))}
      </ul>
    </Tile>
  );
}

function DenseKeyboardSpecimens({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-dense-keyboard-heading`} className="grid gap-4" data-dense-keyboard-specimen>
      <div className="grid gap-1">
        <p className="eyebrow">Dense keyboard</p>
        <h2 id={`${idPrefix}-dense-keyboard-heading`} className="text-tile-title font-semibold">Walk a column, mark a register, open a conversation</h2>
        <p className="text-ui text-muted-foreground">One roving focus model under all three. Specimen data; nothing is sent or saved.</p>
      </div>
      <div className="grid gap-[var(--layout-bento-gap)] xl:grid-cols-3">
        <KeyboardGridSpecimen idPrefix={idPrefix} />
        <AttendanceRowSpecimen idPrefix={idPrefix} />
        <RovingListSpecimen idPrefix={idPrefix} />
      </div>
    </section>
  );
}

function DataSpecimens({ idPrefix }: { idPrefix: string }) {
  const chartTokens = TOKEN_GROUPS.data;
  return (
    <section aria-labelledby={`${idPrefix}-data-heading`} className="space-y-4">
      <div><p className="eyebrow">Data visualization</p><h2 id={`${idPrefix}-data-heading`} className="text-tile-title">Reporting and admissions roles</h2></div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">{chartTokens.map((token, index) => <div key={token} className="flex items-center gap-2 rounded-xl border bg-card p-2"><span className="h-8 rounded-control" style={{ width: `${42 + (index % 4) * 12}%`, background: `var(--${token})` }} /><code className="text-key">--{token}</code></div>)}</div>
    </section>
  );
}

function ThemeSpecimen({ tenant }: { tenant: TenantSlug }) {
  const [scope, setScope] = useState<HTMLElement | null>(null);
  const ref = useRef<HTMLElement>(null);
  const instanceId = useId().replaceAll(":", "");
  const idPrefix = `${tenant}-${instanceId}`;
  useLayoutEffect(() => setScope(ref.current), []);

  return (
    <article ref={ref} data-theme-preset={tenant} data-testid={`design-system-${tenant}`} className="min-w-0 space-y-10 overflow-hidden rounded-xl border bg-background p-4 text-foreground sm:p-6">
      <header className="flex items-center gap-4"><img src={TENANT_LOGOS[tenant]} alt={`${TENANT_NAMES[tenant]} logo`} className="size-14 object-contain" /><div><p className="eyebrow">Tenant system</p><h1 className="text-title">{TENANT_NAMES[tenant]}</h1><p className="mt-2 text-ui text-muted-foreground">Complete shared contract, resolved through <code>data-theme-preset</code>.</p></div></header>
      <OwnershipLayers tenant={tenant} scope={scope} idPrefix={idPrefix} />
      {(Object.entries(TOKEN_GROUPS) as [TokenGroup, readonly SemanticToken[]][]).map(([group, tokens]) => <section key={group} aria-labelledby={`${idPrefix}-${group}`} className="space-y-3"><h2 id={`${idPrefix}-${group}`} className="text-tile-title capitalize">{group}</h2><div className="grid gap-2 xl:grid-cols-2">{tokens.map((token) => <TokenSwatch key={token} token={token} group={group} scope={scope} />)}</div></section>)}
      <TypographySpecimens idPrefix={idPrefix} />
      <FoundationSpecimens idPrefix={idPrefix} />
      <ControlSpecimens idPrefix={idPrefix} tenant={tenant} />
      <BentoKitSpecimens idPrefix={idPrefix} tenant={tenant} />
      <BoxesSection idPrefix={idPrefix} />
      <VisitSection idPrefix={idPrefix} />
      <PageModelSpecimen idPrefix={idPrefix} />
      <SurfaceGrammarSpecimen idPrefix={idPrefix} />
      <SeatHomeSpecimen idPrefix={idPrefix} />
      <PublicControlSpecimen idPrefix={idPrefix} />
      <SessionIndexSpecimen idPrefix={idPrefix} />
      <WeightVoiceSpecimens idPrefix={idPrefix} />
      <RegistrarWeightVoiceSpecimen idPrefix={idPrefix} />
      <ApplicationSpecimens idPrefix={idPrefix} />
      <RecordSpecimens idPrefix={idPrefix} />
      <DataSpecimens idPrefix={idPrefix} />
      <DenseWorkspaceSpecimen idPrefix={idPrefix} />
      <DenseKeyboardSpecimens idPrefix={idPrefix} />
      <RegistrarRecordSpecimen idPrefix={idPrefix} />
      <details className="rounded-xl border bg-card p-4"><summary className="cursor-pointer font-semibold">Contract inventory</summary><p className="mt-2 text-ui text-muted-foreground">{FOUNDATION_TOKENS.length} foundations · {Object.values(TOKEN_GROUPS).flat().length} semantic roles · {COMPONENT_ALIASES.length} component aliases</p></details>
    </article>
  );
}

export function DesignSystemPage() {
  const originalPreset = useRef(document.documentElement.getAttribute("data-theme-preset"));
  const initial = (originalPreset.current ?? TENANT_SLUGS[0]) as TenantSlug;
  const [activeTenant, setActiveTenant] = useState<TenantSlug>(initial);
  const [compare, setCompare] = useState(false);
  const visibleTenants = compare ? TENANT_SLUGS : [activeTenant];
  const [params] = useSearchParams();

  useEffect(() => () => {
    if (originalPreset.current) {
      document.documentElement.setAttribute("data-theme-preset", originalPreset.current);
    } else {
      applyTenantTheme();
    }
  }, []);

  // One visit state alone (V), and one box view alone, in the tenant the hostname chose.
  if (params.has("visits")) return <VisitFocus tenant={initial} />;
  if (params.has("boxes"))
    return (
      <main className="min-h-screen bg-background px-4 py-6 text-foreground sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1200px]"><BoxesFocus tenant={initial} /></div>
      </main>
    );

  return (
    <main className="min-h-screen bg-background px-4 py-6 text-foreground sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1800px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div><p className="eyebrow">Internal maintenance surface</p><h1 className="text-title">IMAM design system</h1><p className="mt-2 max-w-2xl text-ui text-muted-foreground">Inspect the shared foundations and semantic contract. This page changes presentation only and is not a runtime theme editor.</p></div>
          <div className="flex flex-wrap items-center gap-2" aria-label="Design system display controls">
            {TENANT_SLUGS.map((tenant) => <Button key={tenant} type="button" variant={!compare && activeTenant === tenant ? "default" : "outline"} aria-pressed={!compare && activeTenant === tenant} onClick={() => { setCompare(false); setActiveTenant(tenant); applyTenantTheme(tenant); }}>{TENANT_NAMES[tenant]}</Button>)}
            <Button type="button" variant={compare ? "default" : "outline"} aria-pressed={compare} onClick={() => setCompare((value) => !value)}>Side by side</Button>
          </div>
        </header>
        <div className={compare ? "grid items-start gap-6 lg:grid-cols-2" : "grid"}>{visibleTenants.map((tenant) => <ThemeSpecimen key={tenant} tenant={tenant} />)}</div>
      </div>
    </main>
  );
}
